/**
 * Parses a 9-character hex color string (e.g., `#RRGGBBAA`) and converts it to an `rgba()` string.
 *
 * @param {string} hex - The hex color string to parse. Must be 9 characters long, including the `#` prefix.
 *   - `#RRGGBBAA` format:
 *     - `RR`: Red channel in hexadecimal (00–FF).
 *     - `GG`: Green channel in hexadecimal (00–FF).
 *     - `BB`: Blue channel in hexadecimal (00–FF).
 *     - `AA`: Alpha channel in hexadecimal (00–FF), where `00` is fully transparent and `FF` is fully opaque.
 *
 * @returns {string} The `rgba()` string representation of the color.
 *   - Format: `rgba(r, g, b, a)`
 *     - `r`, `g`, `b`: Integer values for red, green, and blue channels (0–255).
 *     - `a`: Floating-point alpha value (0–1).
 *   - If the input is not a valid 9-character hex string, the original input string is returned.
 *
 * @example
 * // Convert a hex color with alpha to rgba
 * parseHexWithAlpha('#FF573380'); // Returns "rgba(255, 87, 51, 0.5)"
 *
 * @example
 * // Handle invalid input gracefully
 * parseHexWithAlpha('#FF5733'); // Returns "#FF5733"
 */
export function parseHexWithAlpha(hex: string) {
  if (hex.includes("rgba") || hex.length !== 9) {
    return hex;
  }
  // e.g., #RRGGBBAA

  // Extract individual R, G, B, and A values for flexibility
  const r = Number.parseInt(hex.substring(1, 3), 16);
  const g = Number.parseInt(hex.substring(3, 5), 16);
  const b = Number.parseInt(hex.substring(5, 7), 16);
  const a = (Number.parseInt(hex.substring(7, 9), 16) / 255).toFixed(2);

  return `rgba(${r}, ${g}, ${b}, ${a})`;
}
